import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, DatePicker, Descriptions, Input, Modal, Radio, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { COMPETENCY_PERMS as P, useCompetencyAction, useCompetencyCan, useEffectiveness } from '@/hooks/useCompetency';
import { competencyService, type EffectivenessLine } from '@/services/competency.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_TRAINING_EFFECTIVENESS } from '@/constants/document-entity-types';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, EFFECTIVENESS_METHODS, EFFECTIVENESS_SCORES, STATUS_COLOR } from './competencyDisplay';

const { Text } = Typography;
type Draft = Partial<EffectivenessLine>;
const EDITABLE: Array<keyof EffectivenessLine> = ['method', 'score', 'evaluator_name', 'evaluated_at', 'remarks', 'follow_up'];

const EffectivenessDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canAck = useCompetencyCan(P.assessmentClose, P.trainingManage);
  const { data: ev, isLoading, error } = useEffectiveness(id);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [completing, setCompleting] = useState(false);
  const [effective, setEffective] = useState<boolean>();
  const [comments, setComments] = useState('');
  const [justification, setJustification] = useState('');
  const [acking, setAcking] = useState(false);
  const [ackNotes, setAckNotes] = useState('');

  useEffect(() => {
    if (!ev) return;
    setDrafts({});
    setComments(ev.comments ?? '');
    setJustification(ev.delay_justification ?? '');
  }, [ev]);

  const lines = ev?.lines ?? [];
  const isOpen = ev?.status === 'Open';
  const value = <K extends keyof EffectivenessLine>(l: EffectivenessLine, k: K) => (k in (drafts[l.id] ?? {}) ? drafts[l.id][k] : l[k]) as EffectivenessLine[K];
  const set = (lineId: string, patch: Draft) => setDrafts((d) => ({ ...d, [lineId]: { ...d[lineId], ...patch } }));
  const changed = useMemo(() => Object.entries(drafts).filter(([, d]) => EDITABLE.some((k) => k in d)), [drafts]);

  const save = useCompetencyAction(
    () => competencyService.saveEffectivenessLines(id as string, changed.map(([lineId, d]) => ({ id: lineId, ...d }))),
    'Evaluation saved'
  );
  const complete = useCompetencyAction(
    () =>
      competencyService.completeEffectiveness(id as string, {
        overall_effective: effective as boolean,
        comments: comments || null,
        delay_justification: justification || null,
      }),
    'Evaluation completed'
  );
  const acknowledge = useCompetencyAction(() => competencyService.acknowledgeEffectiveness(id as string, ackNotes || null), 'Evaluation acknowledged');

  if (isLoading) return <Spin style={{ margin: 48 }} />;
  if (!ev) {
    return <Alert type="error" showIcon style={{ margin: 24 }} message={(error as Error)?.message || 'Evaluation not found or not accessible'} />;
  }

  const overdue = isOpen && dayjs().startOf('day').isAfter(dayjs(ev.due_date));
  const applyToAll = (patch: Draft) => lines.forEach((l) => set(l.id, patch));

  const columns: ColumnsType<EffectivenessLine> = [
    { title: 'Emp. No.', dataIndex: 'employee_number', width: 100 },
    { title: 'Trainee', dataIndex: 'employee_name', render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/employees/${r.employee_id}`)}>{v}</a> },
    {
      title: 'Method',
      width: 190,
      render: (_, r) =>
        isOpen ? (
          <Select size="small" style={{ width: '100%' }} value={value(r, 'method') ?? undefined} onChange={(v) => set(r.id, { method: v })} options={EFFECTIVENESS_METHODS} />
        ) : (
          EFFECTIVENESS_METHODS.find((m) => m.value === r.method)?.label ?? '-'
        ),
    },
    {
      title: 'Score',
      width: 130,
      render: (_, r) =>
        isOpen ? (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={value(r, 'score') ?? undefined}
            onChange={(v: number) => set(r.id, { score: v, follow_up: v === 0 ? 'Retrain' : v === 1 ? 'Supervised work' : 'None' })}
            options={EFFECTIVENESS_SCORES.map((s) => ({ value: s.value, label: s.label.split(' (')[0] }))}
          />
        ) : (
          <Tag color={Number(r.score) >= 2 ? 'green' : 'red'}>{r.score ?? '-'}</Tag>
        ),
    },
    {
      title: 'Follow-up',
      width: 150,
      render: (_, r) =>
        isOpen ? (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={value(r, 'follow_up')}
            onChange={(v) => set(r.id, { follow_up: v })}
            options={['None', 'Retrain', 'Supervised work'].map((s) => ({ value: s, label: s }))}
          />
        ) : (
          r.follow_up
        ),
    },
    {
      title: 'Evaluated by',
      width: 170,
      render: (_, r) =>
        isOpen ? (
          <Input size="small" placeholder={r.evaluator_display ?? 'Name'} value={value(r, 'evaluator_name') ?? ''} onChange={(e) => set(r.id, { evaluator_name: e.target.value })} />
        ) : (
          r.evaluator_name || r.evaluator_display || '-'
        ),
    },
    {
      title: 'Date',
      width: 140,
      render: (_, r) => {
        const v = value(r, 'evaluated_at');
        return isOpen ? (
          <DatePicker size="small" value={v ? dayjs(v) : null} onChange={(d) => set(r.id, { evaluated_at: d ? d.format('YYYY-MM-DD') : null })} />
        ) : (
          formatDate(v)
        );
      },
    },
    {
      title: 'Remarks',
      render: (_, r) =>
        isOpen ? <Input size="small" value={value(r, 'remarks') ?? ''} onChange={(e) => set(r.id, { remarks: e.target.value })} /> : r.remarks,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title={`Training effectiveness - ${ev.session_number}`}
        extra={
          <Space wrap>
            <Tag color={STATUS_COLOR[ev.status]}>{ev.status}</Tag>
            <ReportDocumentActions
              reportKey="competency-training-effectiveness"
              params={{ id: ev.id }}
              documentId={ev.id}
              sendPermissions={[P.trainingManage]}
            />
            <Button onClick={() => navigate(`${COMPETENCY_BASE}/sessions/${ev.session_id}`)}>Attendance</Button>
            {isOpen && (
              <>
                <Button icon={<SaveOutlined />} disabled={!changed.length} loading={save.isPending} onClick={() => save.mutate()}>
                  Save ({changed.length})
                </Button>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setCompleting(true)}>
                  Complete
                </Button>
              </>
            )}
            {ev.status === 'Completed' && canAck && (
              <Button type="primary" onClick={() => setAcking(true)}>
                HR acknowledge
              </Button>
            )}
          </Space>
        }
      />
      {overdue && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`This evaluation was due on ${formatDate(ev.due_date)}. A justification for the delay is required to complete it.`}
        />
      )}
      <Card style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, md: 3 }}>
          <Descriptions.Item label="Training">{`${ev.course_code ?? ''} ${ev.course_title}`.trim()}</Descriptions.Item>
          <Descriptions.Item label="Training date">{formatDate(ev.session_date)}</Descriptions.Item>
          <Descriptions.Item label="Trainer">{ev.trainer_display ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Evaluation due">{formatDate(ev.due_date)}</Descriptions.Item>
          {ev.status !== 'Open' && (
            <Descriptions.Item label="Training effective">{Number(ev.overall_effective) ? 'Yes' : 'No'}</Descriptions.Item>
          )}
          {ev.completed_at && <Descriptions.Item label="Completed">{formatDate(ev.completed_at)}</Descriptions.Item>}
          {ev.comments && <Descriptions.Item label="Comments" span={3}>{ev.comments}</Descriptions.Item>}
          {ev.delay_justification && <Descriptions.Item label="Delay justification" span={3}>{ev.delay_justification}</Descriptions.Item>}
          {ev.hr_ack_at && (
            <Descriptions.Item label="HR acknowledged" span={3}>
              {`${ev.hr_ack_by_name ?? ''} on ${formatDate(ev.hr_ack_at)}${ev.hr_ack_notes ? ` - ${ev.hr_ack_notes}` : ''}`}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
      <Card
        title={`Trainees (${lines.length})`}
        style={{ marginBottom: 16 }}
        extra={
          isOpen ? (
            <Space>
              <Text type="secondary">Apply to all:</Text>
              <Select size="small" placeholder="Method" style={{ width: 190 }} options={EFFECTIVENESS_METHODS} onChange={(v) => applyToAll({ method: v })} />
              <DatePicker size="small" placeholder="Date" onChange={(d) => d && applyToAll({ evaluated_at: d.format('YYYY-MM-DD') })} />
            </Space>
          ) : null
        }
      >
        <Table rowKey="id" size="small" pagination={false} dataSource={lines} columns={columns} scroll={{ x: 1100 }} />
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Scores: {EFFECTIVENESS_SCORES.map((s) => s.label).join(' | ')}. A score of 0 adds a re-training line to the annual schedule.
        </Text>
      </Card>
      <EntityAttachmentsCard entityType={DOCUMENT_ENTITY_TYPE_TRAINING_EFFECTIVENESS} entityId={ev.id} title="Evidence (tests, checklists)" readOnly={!isOpen} />

      <Modal
        title="Complete effectiveness evaluation"
        open={completing}
        onCancel={() => setCompleting(false)}
        okText="Complete"
        okButtonProps={{ disabled: effective === undefined || (overdue && !justification.trim()) }}
        confirmLoading={complete.isPending || save.isPending}
        onOk={async () => {
          if (changed.length) await save.mutateAsync();
          await complete.mutateAsync();
          setCompleting(false);
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>Was the training effective overall?</Text>
          <Radio.Group value={effective} onChange={(e) => setEffective(e.target.value)} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
          <Text strong>Comments</Text>
          <Input.TextArea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} />
          {overdue && (
            <>
              <Text strong>Justification for the delay</Text>
              <Input.TextArea rows={3} value={justification} onChange={(e) => setJustification(e.target.value)} />
            </>
          )}
        </Space>
      </Modal>

      <Modal
        title="HR acknowledgement"
        open={acking}
        onCancel={() => setAcking(false)}
        okText="Acknowledge"
        confirmLoading={acknowledge.isPending}
        onOk={async () => {
          await acknowledge.mutateAsync();
          setAcking(false);
        }}
      >
        <Input.TextArea rows={3} placeholder="Notes (optional)" value={ackNotes} onChange={(e) => setAckNotes(e.target.value)} />
      </Modal>
    </div>
  );
};

export default EffectivenessDetailPage;
